import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { MainHeadingText, SecondaryHeadingText, BodySmallText, BodyBigBoldText, BodyBigRegularText } from '../../../../styles/Text';
import { ChooseDateComponent } from '../create/ui/CreateNewTask/ChooseDate/ChooseDateComponent';
import { ChooseDurationComponent } from '../create/ui/CreateNewTask/ChooseDuration/ChooseDurationComponent';
import { ChooseMinPointsComponent } from '../create/ui/CreateNewTask/ChooseMinPoints/ChooseMinPointsComponent';
import { convertMinutesToDurationFormat } from '../../../../utils/convertMinutesToDurationFormat';
import { convertDatesToDurationFormat } from '../../../../utils/convertDatesToDurationFormat';
import { EventType } from '../../../../core/types/event.interface';
import { TaskFormInput } from '../../../common/main/form/TaskFormInput';
import { BlueButton } from '../../../common/main/button/BlueButton';
import { SelectStyled, TaskFormSelect } from '../../../common/main/form/TaskFormSelect';
import { TaskFormTextAreaInput } from '../../../common/main/form/TaskFormTextArea';
import { eventApi } from '../../../../core/api/rtk-query/event.api';
import { groupApi } from '../../../../core/api/rtk-query/group.api';
import { problemApi } from '../../../../core/api/rtk-query/problem.api';
import { userApi } from '../../../../core/api/rtk-query/user.api';
import { daysFromToInitialValue } from '../../../../utils/daysFromToInitialValue';
import { ChooseTask } from '../create/ui/ChooseTask';
import { IProblem } from '../../../../core/types/problem.interface';
import {
  CreateTaskFormStyled,
  CountSymbols,
  ButtonStyled,
  CreateTaskFormContainer,
  CreateTaskFormField,
  RadioButtonStyled,
  AssigneeContainerStyled,
} from '../create/ui/CreateNewTask/Styles';

dayjs.extend(utc);

const StyledPageContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-start;
  gap: 57px;
  flex: 1 0 0;
  align-self: stretch;
`;

const RightLine = styled.div`
  border-right: 1px solid #e1e1e1;
  height: 100%;
`;

interface IEditTaskFields {
  name: string;
  type: EventType;
  description: string;
}

const universityEventTypesDecriptions: { name: EventType; label: string }[] = [
  {
    name: EventType.Homework,
    label: 'Домашнее задание',
  },
  {
    name: EventType.Contest,
    label: 'Контрольная работа',
  },
  {
    name: EventType.Milestone,
    label: 'Рубежная работа',
  },
  {
    name: EventType.Exam,
    label: 'Экзамен',
  },
];

export const EditTaskPage = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [selectedTasks, setSelectedTasks] = useState<IProblem[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  const { data: eventData, isLoading: eventLoading } = eventApi.useGetEventQuery(taskId || '', { skip: !taskId });
  
  // Загружаем ПОЛНУЮ информацию о задачах мероприятия через специальный endpoint
  // Этот endpoint загружает только задачи данного мероприятия (по их хешам)
  const { 
    data: problemsData, 
    isLoading: problemsLoading, 
    isError: problemsError,
    refetch: refetchProblems 
  } = problemApi.useGetProblemsByEventIdQuery(taskId || '', { 
    skip: !taskId,
    // Повторная попытка при ошибке
    refetchOnMountOrArgChange: true,
  });
  const [updateEvent] = eventApi.useUpdateEventMutation();
  const [addProblemToEvent] = eventApi.useAddProblemToEventMutation();
  const [removeProblemFromEvent] = eventApi.useRemoveProblemFromEventMutation();
  const [grantEventAccessForGroup] = eventApi.useGrantEventAccessForGroupMutation();
  const [addEventToUsers] = eventApi.useAddEventToUsersMutation();
  const [removeUserFromEvent] = eventApi.useRemoveUserFromEventMutation();
  const [removeEventFromGroup] = eventApi.useRemoveEventFromGroupMutation();

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IEditTaskFields>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: EventType.Homework,
      description: '',
    },
  });

  const [eventDates, setEventDates] = useState({
    startTime: daysFromToInitialValue.startTimeInitialValueISO,
    endTime: daysFromToInitialValue.endTimeInitialValueISO,
  });

  const [minimumPoints, setMinimumPoints] = useState<number>(5);
  const isHomework = getValues('type') === EventType.Homework;

  const [eventDuration, setEventDuration] = useState<string>(
    convertDatesToDurationFormat(eventDates.startTime, eventDates.endTime),
  );

  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [studentSearch, setStudentSearch] = useState<string>('');
  const { data: groupActiveData } = groupApi.useGetActiveGroupQuery();
  const { data: eventUsers } = eventApi.useGetUsersInEventQuery(
    { eventId: taskId || '', pageNumber: 1, pageSize: 100 },
    { skip: !taskId }
  );
  const [triggerSearchUsers, { data: searchResults }] = userApi.useLazySearchUsersQuery();

  // Загружаем данные события
  useEffect(() => {
    if (eventData) {
      setValue('name', eventData.name);
      setValue('type', EventType[eventData.type as keyof typeof EventType]);
      setValue('description', eventData.description || '');
      setEventDates({
        startTime: eventData.startTime,
        endTime: eventData.endTime,
      });
      setMinimumPoints(eventData.minimumPointsToPass || 5);
      setEventDuration(eventData.duration || '00:45:00');
      
      // Загружаем курс если есть
      if (eventData.groupId) {
        setSelectedGroup(eventData.groupId);
      }
      
      // Загружаем задачи события - обогащаем ПОЛНЫМИ данными из problemsData
      if (eventData.problems && eventData.problems.length > 0) {
        // Если задачи уже загружены из внешнего сервиса
        if (problemsData?.problems && !problemsLoading) {
          const problems = eventData.problems.map((ep: any) => {
            // Ищем полную информацию о задаче по hash
            const fullProblem = problemsData.problems.find((p: any) => p.hash === ep.hash);
            
            if (fullProblem) {
              // Используем полную информацию о задаче
              return {
                ...fullProblem,
                id: ep.id,
                points: ep.points, // points берем из мероприятия, а не из банка задач
              } as IProblem;
            }
            
            // Если задачу не нашли в problemsData - показываем хотя бы hash
            return {
              id: ep.id,
              hash: ep.hash,
              name: `Задача ${ep.hash.substring(0, 12)}...`,
              description: '',
              russianCondition: '',
              englishCondition: '',
              points: ep.points,
            } as IProblem;
          });
          
          setSelectedTasks(problems);
          setTasksLoading(false);
        } else if (problemsError) {
          // Если произошла ошибка - показываем уведомление
          // но все равно отображаем хеши чтобы можно было работать с мероприятием
          toast.error('Ошибка загрузки условий задач из банка. Вы можете продолжить работу или повторить попытку.', {
            duration: 5000,
          });
          
          const problems = eventData.problems.map((ep: any) => ({
            id: ep.id,
            hash: ep.hash,
            name: `⚠️ Ошибка загрузки задачи ${ep.hash.substring(0, 8)}...`,
            description: 'Не удалось загрузить условие задачи',
            russianCondition: '',
            englishCondition: '',
            points: ep.points,
          } as IProblem));
          
          setSelectedTasks(problems);
          setTasksLoading(false);
        }
        // Если problemsLoading === true - оставляем tasksLoading = true
      } else {
        // Если задач нет - убираем лоадер
        setSelectedTasks([]);
        setTasksLoading(false);
      }
      
      // Загружаем студентов, которым назначено задание
      // ВАЖНО: Если мероприятие назначено курсу, студентов не показываем,
      // потому что они добавляются автоматически через курс и не должны редактироваться вручную
      if (!eventData.groupId && eventUsers?.users) {
        setSelectedStudents(eventUsers.users.filter((u: any) => u.role === 'Student'));
      } else {
        setSelectedStudents([]);
      }
    }
  }, [eventData, problemsData, problemsLoading, problemsError, eventUsers, setValue]);

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value as EventType;
    setValue('type', selectedType);

    if (selectedType === EventType.Homework) {
      const convertedDuration = convertDatesToDurationFormat(eventDates.startTime, eventDates.endTime);
      setEventDuration(convertedDuration);
    }

    if (selectedType !== EventType.Homework) {
      setEventDuration('00:45:00');
    }
  };

  const handleDatesChange = (dates: { fromDate: string; fromTime: string; toDate: string; toTime: string }) => {
    const startTime = `${dates.fromDate}T${dates.fromTime}`;
    const endTime = `${dates.toDate}T${dates.toTime}`;

    const userUtcOffset = dayjs().utcOffset();

    const startTimeISO = dayjs(startTime, 'DD.MM.YYYYTHH:mm').subtract(userUtcOffset, 'minute').format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endTimeISO = dayjs(endTime, 'DD.MM.YYYYTHH:mm').subtract(userUtcOffset, 'minute').format('YYYY-MM-DDTHH:mm:ss[Z]');

    setEventDates({ startTime: startTimeISO, endTime: endTimeISO });

    if (isHomework) {
      const convertedDatesToDuration = convertDatesToDurationFormat(startTimeISO, endTimeISO);
      setEventDuration(convertedDatesToDuration);
    }
  };

  const handleDurationChange = (durationNumber: number) => {
    const durationFormatted = convertMinutesToDurationFormat(durationNumber);
    setEventDuration(durationFormatted);
  };

  const handleMinPointsChange = (changedPoints: number) => {
    setMinimumPoints(changedPoints);
  };

  const onSubmit: SubmitHandler<IEditTaskFields> = async data => {
    if (!selectedTasks.length) {
      toast.error('Не выбрано ни одной задачи!');
      return;
    }
    if (isNaN(minimumPoints) || minimumPoints <= 0) {
      toast.error('Ошибка в минимальном количестве баллов!');
      return;
    }
    try {
      // 1. Обновляем основные данные мероприятия
      await updateEvent({
        id: taskId!,
        name: data.name,
        description: data.description,
        type: data.type,
        startTime: eventDates.startTime,
        endTime: eventDates.endTime,
        duration: eventDuration,
        minimumPointsToPass: minimumPoints,
        groupId: selectedGroup || null,
      }).unwrap();

      // 2. Синхронизируем задачи
      const currentProblems = eventData?.problems || [];
      const currentProblemsByHash = new Map<string, any>(
        currentProblems.map((problem: any) => [problem.hash, problem])
      );
      const currentProblemHashes = currentProblems.map((problem: any) => problem.hash);

      const selectedTasksWithPoints = selectedTasks.map(task => ({
        ...task,
        points: task.points ?? 1,
      }));
      const selectedProblemHashes = selectedTasksWithPoints.map(task => task.hash);

      console.log('=== СИНХРОНИЗАЦИЯ ЗАДАЧ ===');
      console.log('Текущие задачи в мероприятии:', currentProblemHashes);
      console.log('Выбранные задачи:', selectedProblemHashes);

      // Удаляем задачи которых больше нет
      const hashesToRemove = currentProblemHashes.filter(hash => !selectedProblemHashes.includes(hash));
      console.log('Задачи для удаления:', hashesToRemove);
      for (const hash of hashesToRemove) {
        if (hash) {
          console.log('Удаляем задачу:', hash);
          await removeProblemFromEvent({ eventId: taskId!, hash }).unwrap();
        }
      }

      // Добавляем новые задачи
      const hashesAlreadyAttached = new Set(currentProblemHashes);
      const tasksToAdd = selectedTasksWithPoints.filter(task => 
        task.hash && // hash должен существовать
        task.hash.trim() !== '' && // hash не должен быть пустым
        !hashesAlreadyAttached.has(task.hash) // hash не должен быть уже добавлен
      );
      
      console.log('Задачи для добавления:', tasksToAdd.map(t => ({ hash: t.hash, points: t.points })));
      
      for (const task of tasksToAdd) {
        if (!task.hash || task.hash.trim() === '') {
          console.warn('Пропускаем задачу без валидного hash:', task);
          continue;
        }
        console.log('Добавляем задачу:', task.hash, 'баллы:', task.points);
        await addProblemToEvent({ eventId: taskId!, hash: task.hash, points: task.points ?? 1 }).unwrap();
        console.log('Задача добавлена успешно:', task.hash);
      }
      
      console.log('=== СИНХРОНИЗАЦИЯ ЗАДАЧ ЗАВЕРШЕНА ===');

      const isValidId = (value?: string) =>
        Boolean(value && value !== '00000000-0000-0000-0000-000000000000' && value !== 'undefined');

      // 3. Синхронизируем студентов ТОЛЬКО если мероприятие НЕ назначено курсу
      // Если назначено курсу - студенты управляются через курс автоматически
      if (!selectedGroup) {
        // ВАЖНО: Фильтруем только студентов, не трогаем преподавателей
        const currentStudents = eventUsers?.users?.filter((u: any) => u.role === 'Student') || [];
        const currentStudentIds = currentStudents.map((u: any) => u.id).filter((id: string) => id && isValidId(id));
        const newUserIds = selectedStudents.map(s => s.id).filter(isValidId);
        
        // Удаляем студентов которых больше нет
        const studentsToRemove = currentStudentIds.filter((id: string) => !newUserIds.includes(id));
        for (const userId of studentsToRemove) {
          if (isValidId(userId)) {
            // Удаляем студента из мероприятия
            try {
              await removeUserFromEvent({ eventId: taskId!, userId }).unwrap();
            } catch (err) {
              // Игнорируем ошибки удаления (возможно, студент уже удален)
              console.warn('Failed to remove user from event:', err);
            }
          }
        }
        
        // Добавляем новых студентов
        const studentsToAdd = newUserIds.filter(id => isValidId(id) && !currentStudentIds.includes(id));
        if (studentsToAdd.length > 0) {
          await addEventToUsers({ id: taskId!, userIds: studentsToAdd }).unwrap();
        }
      }
      // Если назначено курсу - студентов не трогаем, они управляются через курс

      // 4. Синхронизируем группу (если группа изменилась)
      if (eventData && selectedGroup !== eventData.groupId) {
        // Если была группа - удаляем связь
        if (eventData.groupId) {
          try {
            await removeEventFromGroup({ eventId: taskId!, groupId: eventData.groupId }).unwrap();
          } catch (err) {
            // Игнорируем ошибки удаления
            console.warn('Failed to remove event from group:', err);
          }
        }
        // Если выбрана новая группа - добавляем
        if (selectedGroup) {
          await grantEventAccessForGroup({ eventId: taskId!, groupId: selectedGroup }).unwrap();
        }
      }

      toast.success('Мероприятие обновлено');
      navigate(-1);
    } catch (error: any) {
      console.error('Error updating event:', error);
      toast.error(error?.data?.message || 'Произошла ошибка при обновлении мероприятия');
    }
  };

  if (eventLoading) {
    return <MainHeadingText>Загрузка...</MainHeadingText>;
  }

  if (!eventData) {
    return <MainHeadingText>Задание не найдено</MainHeadingText>;
  }

  return (
    <>
      <MainHeadingText style={{ marginBottom: '15px' }}>Редактирование мероприятия</MainHeadingText>

      <StyledPageContent>
        <RightLine>
          <div>
            <SecondaryHeadingText style={{ marginBottom: '15px' }}>О задании</SecondaryHeadingText>
            <CreateTaskFormStyled onSubmit={handleSubmit(onSubmit)}>
              <CreateTaskFormContainer>
                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>
                      Тип задания <span style={{ color: '#F3163E' }}>*</span>
                    </BodyBigBoldText>
                  </label>
                  <TaskFormSelect
                    options={universityEventTypesDecriptions}
                    value={watch('type')}
                    onChange={handleEventTypeChange}
                  />
                </CreateTaskFormField>

                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>
                      Название задания <span style={{ color: '#F3163E' }}>*</span>
                    </BodyBigBoldText>
                  </label>
                  <TaskFormInput
                    {...register('name', { required: true })}
                    name="name"
                    placeholder="Введите название"
                    maxLength={50}
                    minLength={3}
                  />
                </CreateTaskFormField>

                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>
                      Описание <span style={{ color: '#F3163E' }}>*</span>
                    </BodyBigBoldText>
                  </label>
                  <TaskFormTextAreaInput
                    {...register('description', { minLength: 5, required: true })}
                    name="description"
                    placeholder="Введите описание"
                    maxLength={300}
                    minLength={5}
                  />
                  <CountSymbols>
                    <BodySmallText
                      style={{
                        color: errors.description?.type === 'maxLength' ? 'red' : '#787676',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {watch('description').length}/300
                    </BodySmallText>
                  </CountSymbols>
                  <ChooseMinPointsComponent selectedTasks={selectedTasks} onMinPointsChange={handleMinPointsChange} />
                </CreateTaskFormField>

                {/* Даты - вверху, они общие для всех */}
                <CreateTaskFormField>
                  <ChooseDateComponent onDatesChange={handleDatesChange} />
                </CreateTaskFormField>

                {/* Назначение курсу */}
                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>Назначить курсу</BodyBigBoldText>
                  </label>
                  <SelectStyled value={selectedGroup} onChange={e => {
                    setSelectedGroup(e.target.value);
                    // При выборе курса очищаем список студентов
                    if (e.target.value) {
                      setSelectedStudents([]);
                    }
                  }}>
                    <option value="">Не выбрано</option>
                    {groupActiveData?.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </SelectStyled>
                  <BodySmallText style={{ marginTop: 4, color: '#667' }}>
                    Мероприятие может быть привязано только к одному курсу
                  </BodySmallText>
                </CreateTaskFormField>

                {/* Назначение студентам - только если мероприятие НЕ назначено курсу */}
                {!selectedGroup && (
                  <CreateTaskFormField>
                    <label>
                      <BodyBigBoldText>Дополнительно назначить студентам</BodyBigBoldText>
                    </label>
                    <TaskFormInput
                      placeholder="Поиск студентов по имени или ISU..."
                      value={studentSearch}
                      onChange={(e: any) => {
                        setStudentSearch(e.target.value);
                        if (e.target.value.length >= 2) {
                          triggerSearchUsers({ name: e.target.value, pageNumber: 1, pageSize: 20 });
                        }
                      }}
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    {searchResults?.users && searchResults.users.length > 0 && studentSearch.length >= 2 && (
                      <div style={{ border: '1px solid #dbe3f6', borderRadius: 8, maxHeight: 200, overflowY: 'auto', background: '#fff', marginBottom: 12 }}>
                        {searchResults.users.map((user: any) => (
                          <div
                            key={user.id}
                            onClick={() => {
                              if (!selectedStudents.find(s => s.id === user.id)) {
                                setSelectedStudents([...selectedStudents, user]);
                              }
                              setStudentSearch('');
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: 14
                            }}
                            onMouseEnter={(e: any) => e.currentTarget.style.background = '#f7f9ff'}
                            onMouseLeave={(e: any) => e.currentTarget.style.background = '#fff'}
                          >
                            {user.name} ({user.email}) {user.isuNumber ? `- ISU: ${user.isuNumber}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedStudents.length > 0 && (
                      <div style={{ marginTop: 12, padding: 12, background: '#f7f9ff', borderRadius: 8 }}>
                        <BodyBigRegularText style={{ marginBottom: 8 }}>Назначено студентам ({selectedStudents.length}):</BodyBigRegularText>
                        {selectedStudents.map((student: any) => (
                          <div key={student.id} style={{ padding: '6px 0', fontSize: 14, color: '#334', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e8f0ff' }}>
                            <span>{student.name} ({student.email})</span>
                            <button
                              type="button"
                              onClick={() => setSelectedStudents(selectedStudents.filter(s => s.id !== student.id))}
                              style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: 18, padding: '0 8px' }}
                              title="Удалить"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CreateTaskFormField>
                )}
                {!isHomework && <ChooseDurationComponent onDurationChange={handleDurationChange} />}
              </CreateTaskFormContainer>
              <ButtonStyled>
                <BlueButton type="submit" style={{ width: '200px', height: '40px' }}>
                  {'Сохранить изменения'}
                </BlueButton>
              </ButtonStyled>
            </CreateTaskFormStyled>
          </div>
        </RightLine>

        <div>
          <SecondaryHeadingText style={{ marginBottom: '15px' }}>Просмотр задач</SecondaryHeadingText>
          {tasksLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#667' }}>
              <div style={{ fontSize: 16, marginBottom: 8 }}>
                {problemsLoading ? '⏳ Загрузка условий задач из банка...' : 'Загрузка задач...'}
              </div>
              <div style={{ fontSize: 14, color: '#999' }}>
                {eventData?.problems?.length > 0 
                  ? `Загружается ${eventData.problems.length} задач(и)...`
                  : 'Подождите...'
                }
              </div>
            </div>
          ) : problemsError ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: 16, marginBottom: 12, color: '#e74c3c' }}>
                ⚠️ Ошибка загрузки условий задач
              </div>
              <div style={{ fontSize: 14, marginBottom: 16, color: '#999' }}>
                Не удалось загрузить полную информацию о задачах из банка задач.
                <br />
                Вы можете повторить попытку или продолжить работу (задачи отображены с ограниченной информацией).
              </div>
              <BlueButton 
                onClick={() => refetchProblems()} 
                style={{ width: '200px', height: '40px' }}
              >
                🔄 Повторить загрузку
              </BlueButton>
              <div style={{ marginTop: 16 }}>
                <ChooseTask selectTasks={setSelectedTasks} selectedTasks={selectedTasks} />
              </div>
            </div>
          ) : (
            <ChooseTask selectTasks={setSelectedTasks} selectedTasks={selectedTasks} />
          )}
        </div>
      </StyledPageContent>
    </>
  );
};
